<script setup lang="ts">
import { toast } from '@/shared/toast'
import { invariant } from '@/shared/utils/typeAssertions'
import IconSprite from '@/uiKit/IconSprite.vue'
import { onClickOutside, useDebounce } from '@vueuse/core'
import type { Api } from '@zag-js/tree-view'
import { computed, nextTick, ref, useId, useTemplateRef, watch } from 'vue'
import { FOLDER_NAME_FALLBACK } from '../Project/constants'
import FolderDropdown from '../Project/Folders/FolderDropdown.vue'
import { useFolderStore } from '../Project/Folders/useFolderStore'
import { useWorkspaceContext } from '../Workspaces/useWorkspaceContext'
import { useProjectCreateModal } from './ProjectCreateModal.vue'
import { type ProjectTreeNode } from './ProjectTree.vue'
import ProjectTreeNodeProject, { DRAG_DATA_TYPE } from './ProjectTreeNodeProject.vue'
import { useMoveProject } from './useMoveProject'
import { useProjectTreeDragContext } from './useProjectTreeContext'

/**
 * This component renders a single node in the project tree. It could be a folder
 * or it could be a project.
 *
 * See https://zagjs.com/components/vue/tree-view for information on how to use
 * the headless Tree View component provided by zag-js.
 */

const props = defineProps<{
  node: ProjectTreeNode
  indexPath: number[]
  api: Api
  editingId: string | null
}>()

const emit = defineEmits<{
  rename: [folderId: string]
  'manage-permissions': [folderId: string]
  'clear-editing': []
}>()

const nodeProps = computed(() => ({
  indexPath: props.indexPath,
  node: props.node,
}))

const nodeState = computed(() => props.api.getNodeState(nodeProps.value))

const MAX_FOLDER_DEPTH = 6

const folderOrProjectRef = useTemplateRef('root')
/**
 * When the node is focused, we need to scroll it into view so that the user's focus
 * doesn't disappear off screen for trees with a large number of nodes.
 */
watch(
  () => nodeState.value.focused,
  (focused) => {
    if (focused) {
      if (!folderOrProjectRef.value) {
        return
      }

      /**
       * The ref could be a component instance or a DOM element. If it's a component
       * instance, we need to access the DOM element via the $el property.
       */
      const rootEl =
        folderOrProjectRef.value instanceof Element
          ? folderOrProjectRef.value
          : folderOrProjectRef.value?.$el
      if (!rootEl) {
        return
      }

      rootEl.scrollIntoView({ behavior: 'instant', block: 'nearest' })
    }
  },
)

const { workspaceId } = useWorkspaceContext()
const folderStore = useFolderStore()
const onCreateSubfolder = async () => {
  const res = await folderStore.createFolder({
    parentFolderId: props.node.id,
    workspaceId: workspaceId.value,
  })

  if (!res.ok) {
    toast.error('Failed to create subfolder. Please contact support if the problem persists.')
    return
  }

  // Expand the node to show the new subfolder
  props.api.expand([props.node.id])

  // Emit event to start renaming the new folder
  emit('rename', res.data.id)
}

const projectCreateModal = useProjectCreateModal()
const onCreateAgent = () => {
  projectCreateModal.open(props.node.id)

  // Give time for the modal to open before expanding the node. We want the node
  // expanded so that the user can see the new agent once it's been created.
  setTimeout(() => {
    props.api.expand([props.node.id])
  }, 500)
}

const nodeLabelId = useId()
const { draggedOverFolderId, setDraggedOverFolderId } = useProjectTreeDragContext()
const isDraggingOver = computed(() => draggedOverFolderId.value === props.node.id)

/**
 * If the user has been dragging an agent over a node for a while, expand the node
 * so that the user can drop into a nested folder.
 */
const debouncedDraggingOver = useDebounce(isDraggingOver, 500)
watch(debouncedDraggingOver, (draggingOver) => {
  if (draggingOver) {
    props.api.expand([props.node.id])
  }
})

const moveProject = useMoveProject()
const onDrop = async (e: DragEvent, parentFolderId: string | null) => {
  invariant(e.dataTransfer, 'dataTransfer is required for drop events')

  const projectId = e.dataTransfer.getData(DRAG_DATA_TYPE)
  const res = await moveProject({
    parentFolderId,
    projectId,
  })

  if (!res.ok) {
    toast.error('Failed to move project. Please contact support if the problem persists.')
    return
  }

  if (parentFolderId) {
    props.api.expand([props.node.id])
  }
}
// Rename functionality
const isRenaming = computed(
  () => props.node.nodeType === 'folder' && props.editingId === props.node.id,
)
const newFolderName = ref(props.node.name || FOLDER_NAME_FALLBACK)
const inputRef = useTemplateRef('folderNameInput')

// When entering rename mode, update the input value and focus
watch(isRenaming, async (renaming) => {
  if (renaming) {
    newFolderName.value = props.node.name || FOLDER_NAME_FALLBACK
    // Wait for the input to be rendered before focusing it
    await nextTick()
    if (inputRef.value) {
      inputRef.value.focus()
      inputRef.value.select()
    }
  }
})

const onStartRename = () => {
  emit('rename', props.node.id)
}

const onSubmitRename = async () => {
  const originalName = props.node.name || FOLDER_NAME_FALLBACK
  const trimmedName = newFolderName.value.trim()
  if (!trimmedName || trimmedName === originalName) {
    newFolderName.value = originalName
    emit('clear-editing')
    return
  }

  const result = await folderStore.renameFolder({
    workspaceId: workspaceId.value,
    folderId: props.node.id,
    name: trimmedName,
  })

  if (!result.ok) {
    toast.error('Failed to rename folder. Please try again or contact support.')
  }

  emit('clear-editing')
}

const onCancelRename = () => {
  emit('clear-editing')
}

/**
 * Handle F2 keypress to start renaming when a tree node is focused
 */
const onNodeKeyDown = (e: KeyboardEvent) => {
  if (e.key === 'F2' && props.node.nodeType === 'folder' && nodeState.value.focused) {
    e.preventDefault()
    e.stopPropagation()
    onStartRename()
  }
}

onClickOutside(inputRef, onSubmitRename)
</script>

<template>
  <div
    v-if="node.nodeType === 'folder'"
    v-bind="api.getBranchProps(nodeProps)"
    ref="root"
    class="relative min-h-7"
    :aria-labelledby="nodeLabelId"
    @keydown="onNodeKeyDown"
  >
    <div
      v-bind="api.getBranchIndentGuideProps(nodeProps)"
      data-test="indent"
      class="absolute top-7 w-px border-l border-border-subtle"
      :style="{
        transform: `translateX(${nodeState.depth * 20 - 6}px)`,
        height: 'calc(100% - 38px)',
      }"
    />
    <div
      v-bind="api.getBranchControlProps(nodeProps)"
      class="group flex h-7 items-center rounded-lg bg-background-transparent pr-1.5 outline-none focus-within:bg-background-transparent-hovered hover:bg-background-transparent-hovered focus-visible:shadow-focus-ring-primary"
      :style="{ paddingLeft: `${(nodeState.depth - 1) * 20}px` }"
      @dragenter="setDraggedOverFolderId(node.id)"
      @drop="onDrop($event, node.id)"
    >
      <span
        v-bind="api.getBranchIndicatorProps(nodeProps)"
        class="mx-1.5"
      >
        <IconSprite
          :icon="nodeState.expanded ? 'chevron-bottom' : 'chevron-right'"
          size="lg"
          class="text-icon-subtlest"
        />
      </span>
      <IconSprite
        icon="folder-fill"
        size="sm"
        class="mr-2 text-icon-subtlest"
      />

      <input
        v-if="isRenaming"
        ref="folderNameInput"
        v-model="newFolderName"
        type="text"
        class="w-full min-w-0 grow rounded border-none bg-background-transparent p-0 text-sm-12px-default text-text-subtle outline-none"
        @keydown.enter="onSubmitRename"
        @keydown.esc.prevent="onCancelRename"
      />
      <div
        v-else
        class="flex h-7 grow items-center"
        :class="[isDraggingOver && 'border-b border-link']"
        v-bind="api.getBranchTextProps(nodeProps)"
        :title="node.name || FOLDER_NAME_FALLBACK"
      >
        <div
          v-bind="api.getBranchTextProps(nodeProps)"
          :id="nodeLabelId"
          class="min-w-0 grow truncate text-sm-12px-default text-text-subtle"
          :title="node.name || FOLDER_NAME_FALLBACK"
        >
          {{ node.name || FOLDER_NAME_FALLBACK }}
        </div>
        <FolderDropdown
          :id="node.id"
          :tabindex="nodeState.focused ? 0 : -1"
          :can-create-subfolder="nodeState.depth < MAX_FOLDER_DEPTH"
          :aria-label="`${node.name || FOLDER_NAME_FALLBACK} folder actions`"
          class="opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 focus-visible:opacity-100"
          @create:subfolder="onCreateSubfolder"
          @create:agent="onCreateAgent"
          @rename="onStartRename"
          @manage-permissions="$emit('manage-permissions', node.id)"
        />
      </div>
    </div>
    <div v-bind="api.getBranchContentProps(nodeProps)">
      <ProjectTreeNode
        v-for="(childNode, index) in node.children"
        :key="childNode.id"
        :node="childNode"
        :index-path="[...indexPath, index]"
        :api="api"
        :editing-id="editingId"
        @rename="$emit('rename', $event)"
        @clear-editing="$emit('clear-editing')"
        @manage-permissions="$emit('manage-permissions', $event)"
      />
    </div>
  </div>
  <!--
  👇 a scroll margin is needed here so that we can see the project's name when scrolling
  👇 down with arrow keys, because the browser will render the project's URL above the
  👇 element.
  -->
  <ProjectTreeNodeProject
    v-else
    v-bind="api.getItemProps(nodeProps)"
    ref="root"
    :project="node"
    class="scroll-m-7"
    :node-state="nodeState"
    :style="{ paddingLeft: `${(nodeState.depth - 1) * 20}px` }"
    @drop="onDrop($event, node.parentFolderId)"
    @dragenter="setDraggedOverFolderId(node.parentFolderId)"
  />
</template>
